import { usePurchaseHistory } from "@/hooks/usePurchaseHistory";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { formatDateWithYear } from "@/utils/dateFormatters";
import BreadcrumbComponent from "@/components/BreadcrumbComponent";
import {
  History,
  CircleCheck,
  ChevronDown,
  ChevronUp,
  CreditCard,
  Banknote,
  AlertTriangle,
} from "lucide-react";
import CardSkeleton from "@/components/CardSkeleton";
import PurchaseHistoryStub from "@/components/PurchaseHistoryStub";
import { Typography } from "@/components/ui/typography";
import { Button } from "@/components/ui/button";

const PurchaseHistory = () => {
  const { purchaseData, isLoading, showMore, setShowMore } = usePurchaseHistory();

  const getPaymentTypeLabel = (type: string) => {
    switch (type) {
      case "card":
        return "Картой";
      case "cashless":
        return "Безналичный";
      case "cash":
        return "Наличные";
      default:
        return type;
    }
  };

  const getPaymentIcon = (type: string) => {
    switch (type) {
      case "card":
        return <CreditCard className="h-4 w-4" />;
      case "cashless":
        return <Banknote className="h-4 w-4" />;
      case "cash":
        return <Banknote className="h-4 w-4" />;
      default:
        return <CreditCard className="h-4 w-4" />;
    }
  };
  return (
    <div className="flex w-full flex-col p-3">
      <BreadcrumbComponent link={"Последние оплаты"} />
      <div className="mb-6 flex items-center gap-2">
        <History className="h-5 w-5 text-primary" />
        <h2 className="text-xl font-medium text-gray-900">Последние оплаты</h2>
      </div>
      <div className="m:grid-cols-1 grid gap-4">
        {!isLoading && (!purchaseData || purchaseData.length === 0) && <PurchaseHistoryStub />}
      </div>
      <div className="grid gap-4 sm:grid-cols-1">
        {isLoading
          ? [...Array(3)].map((_, index) => <CardSkeleton key={index} />)
          : purchaseData &&
            purchaseData.slice(0, showMore ? purchaseData.length : 3).map((purchase) => (
              <Card
                key={purchase.id}
                className={`max-w-[60rem] overflow-hidden ${
                  purchase.total_debt > 0
                    ? "border-l-4 border-l-orange-500"
                    : "border-l-4 border-l-green-500"
                }`}
              >
                <CardHeader className="border-b bg-muted/50 p-4">
                  <div className="flex flex-col gap-2">
                    {purchase.products.map((productItem) => (
                      <div key={productItem.product.id} className="group flex items-center gap-3">
                        <div className="rounded-full bg-primary/10 p-2 sm:p-2">
                          <CircleCheck className="h-5 w-5 min-w-[20px] text-primary" />
                        </div>
                        <Typography
                          variant="h3"
                          className="text-lg font-normal text-gray-800 transition-colors group-hover:text-primary"
                        >
                          {productItem.product.title}
                        </Typography>
                      </div>
                    ))}
                  </div>
                </CardHeader>

                {/* Контент */}
                <CardContent className="space-y-4 p-4">
                  {/* Сумма оплаты */}
                  <div className="flex items-center justify-between">
                    <Typography variant="muted" className="text-muted-foreground">
                      Сумма:
                    </Typography>
                    <div className="inline-flex items-center">
                      <Typography variant="p" className="text-base leading-relaxed">
                        {purchase.total_amount} ₽
                      </Typography>
                    </div>
                  </div>

                  {/* Дата оплаты */}
                  <div className="flex items-center justify-between text-sm">
                    <Typography variant="muted" className="text-muted-foreground">
                      Дата покупки:
                    </Typography>
                    <Typography variant="p" className="text-base leading-relaxed">
                      {formatDateWithYear(purchase.datetime)}
                    </Typography>
                  </div>

                  {/* Детали продуктов, если их больше одного */}
                  {purchase.products.length > 1 && (
                    <div className="flex flex-col gap-2 border-t pt-2">
                      <span className="text-sm text-muted-foreground">Состав покупки:</span>
                      <div className="flex flex-col gap-1.5">
                        {purchase.products.map((item) => (
                          <div
                            key={item.product.id}
                            className="flex flex-wrap items-center justify-between gap-1 text-sm"
                          >
                            <span className="min-w-0 flex-1 text-gray-600">
                              {item.product.title}
                            </span>
                            <span className="shrink-0 font-medium">{item.price} ₽</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Информация о долге */}
                  {purchase.total_debt > 0 && purchase.payments && purchase.payments.length > 0 && (
                    <div className="flex items-center justify-between border-t pt-2">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-orange-500" />
                        <Typography variant="muted" className="text-orange-600">
                          К доплате:
                        </Typography>
                      </div>
                      <Typography variant="p" className="font-semibold text-orange-600">
                        {purchase.total_debt} ₽
                      </Typography>
                    </div>
                  )}

                  {/* Информация об оплатах */}
                  {purchase.payments && purchase.payments.length > 0 ? (
                    <div className="border-t pt-2">
                      <Typography variant="muted" className="mb-2 text-sm text-muted-foreground">
                        История оплат:
                      </Typography>
                      <div className="space-y-2">
                        {purchase.payments.map((payment, index) => (
                          <div
                            key={index}
                            className="flex flex-wrap items-center justify-between gap-1 text-sm"
                          >
                            <div className="flex items-center gap-2">
                              {getPaymentIcon(payment.type)}
                              <span className="text-gray-600">
                                {getPaymentTypeLabel(payment.type)}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {formatDateWithYear(payment.datetime)}
                              </span>
                            </div>
                            <span className="font-medium text-green-600">
                              +{payment.payment_amount} ₽
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : purchase.total_amount > 0 ? (
                    <div className="flex items-center justify-between border-t pt-2">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-orange-500" />
                        <span className="text-sm text-orange-600">Не оплачено:</span>
                      </div>
                      <span className="font-semibold text-orange-600">
                        {purchase.total_amount} ₽
                      </span>
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            ))}

        {purchaseData && purchaseData.length > 3 && (
          <Button
            variant="outline"
            size="sm"
            className="mx-auto mb-4 w-full max-w-md text-gray-600 hover:text-gray-700"
            onClick={() => {
              setShowMore(!showMore);
            }}
          >
            <span className="flex items-center gap-2">
              {showMore ? (
                <>
                  <ChevronUp className="h-4 w-4" />
                  Скрыть
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4" />
                  Показать за более ранние даты
                </>
              )}
            </span>
          </Button>
        )}
      </div>
    </div>
  );
};

export default PurchaseHistory;
